<template>
    <div
        class="string-cell"
        :class="[field.alignment, { 'invalid-value': invalidValue, outlined: outlined, readonly: readonly }]"
        @mouseenter="showOverlay = true"
        @mouseleave="showOverlay = false"
    >
        <!-- Düzenlenmeyen (Görüntüleme) Modu -->
        <template v-if="!editing">
            <a-tooltip :title="value">
                <div class="text-wrapper">
                    {{ value }}
                </div>
            </a-tooltip>
        </template>

        <!-- Düzenleme Modu -->
        <template v-else>
            <input
                type="text"
                v-model="value"
                @blur="debouncedDisableEdit"
                @keyup.enter="debouncedDisableEdit"
                ref="input"
                class="borderless-input"
                :class="{ 'invalid-input': invalidValue }"
            />
        </template>

        <!-- Hover Overlay'de Aksiyon İkonları -->
        <div v-if="!editing && showOverlay && !readonly" class="overlay-actions">
            <a-button icon="edit" size="small" @click="enableEdit" style="margin-right: 4px"></a-button>
            <a-button
                icon="delete"
                type="danger"
                size="small"
                @click="deleteValue"
                style="margin-right: 4px"
            ></a-button>
            <a-button
                icon="mail"
                size="small"
                @click="openMailComposerModal"
                v-if="isEmailField"
                style="margin-right: 4px"
            ></a-button>
            <a-button size="small" @click="openWhatsappModal" class="whatsapp-button" v-if="isPhoneField">
                <WhatsappIcon />
            </a-button>
        </div>

        <!-- WhatsApp Chat Starter Modal -->
        <Modal2
            v-if="showWhatsappChatStarterModal"
            @cancel="showWhatsappChatStarterModal = false"
            :title="$t('whatsappChatStart')"
            :showFooter="false"
            className="whatsapp-chat-starter-modal keydown-prevent-modal"
        >
            <WhatsappChatStarter
                :default-phone-number="value"
                channel-override
                phone-number-override
                @close="showWhatsappChatStarterModal = false"
            />
        </Modal2>

        <!-- 7) Mail Composer Modal -->
        <Modal2
            v-if="showMailComposerModal"
            @cancel="showMailComposerModal = false"
            :title="$t('mail.composeNewEmail')"
            :showFooter="false"
            xlarge
            className="table-mail-composer-modal"
        >
            <MailComposer
                :isNewMail="true"
                :replyContent="''"
                @closeEditor="showMailComposerModal = false"
                :defaultTo="value"
            />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import debounce from "lodash/debounce";
import Modal2 from "@/components/common/Modal2.vue";
import WhatsappChatStarter from "@/views/Pages/Integrations/meta-leads/WhatsappChatStarter.vue";
import WhatsappIcon from "../../../../../components/svg-new-ui/WhatsappIcon.vue";
import MailComposer from "@/views/Pages/Mail/MailComposer.vue";
import { Tooltip } from 'ant-design-vue';

export default Vue.extend({
    name: "StringCell",
    components: {
        Modal2,
        WhatsappChatStarter,
        WhatsappIcon,
        MailComposer,
        'a-tooltip': Tooltip,
    },
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editing: false,
            value: this.record.data[this.field._id] || "",
            invalidValue: false,
            showOverlay: false,
            showWhatsappChatStarterModal: false,
            showMailComposerModal: false,
        };
    },
    computed: {
        isPhoneField() {
            return this.field.fieldType === "phone";
        },
        isEmailField() {
            return this.field.fieldType === "email";
        },
    },
    created() {
        this.debouncedDisableEdit = debounce(this.disableEdit, 300);
    },
    methods: {
        enableEdit() {
            if (this.readonly) {
                return;
            }
            this.editing = true;
            this.$nextTick(() => {
                if (this.$refs.input) {
                    this.$refs.input.focus();
                }
            });
        },
        async disableEdit() {
            this.editing = false;
            try {
                const isChanged = this.record.data[this.field._id] !== this.value;
                if (isChanged) {
                    const isValid = this.validateValue(this.value, this.field.fieldType);
                    if (isValid) {
                        this.invalidValue = false;
                        await this.$emit("update-record", {
                            recordId: this.record._id,
                            fieldId: this.field._id,
                            value: this.value,
                        });
                    } else {
                        this.invalidValue = true;
                        this.$message.error("Geçersiz değer. Lütfen doğru formatı kullanın.");
                    }
                }
            } catch (error) {
                console.error("Kayıt güncellenirken hata oluştu:", error);
                this.$message.error("Kayıt güncellenirken hata oluştu.");
            }
        },
        deleteValue() {
            // İçeriği silip günceller
            this.value = "";
            this.invalidValue = false;
            this.$emit("update-record", {
                recordId: this.record._id,
                fieldId: this.field._id,
                value: "",
            });
        },
        openWhatsappModal() {
            // Sahada telefon numarası değilse kullanıcıya uyarı gösterelim
            if (this.field.fieldType !== "phone") {
                this.$message.error("Bu alan telefon numarası için geçerlidir.");
                return;
            }
            this.showWhatsappChatStarterModal = true;
        },
        openMailComposerModal() {
            if (this.field.fieldType !== "email") {
                this.$message.error("Bu alan e-posta için geçerlidir.");
                return;
            }
            this.showMailComposerModal = true;
        },
        validateValue(value, fieldType) {
            switch (fieldType) {
                case "email":
                    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                case "phone":
                    return /^\+?[0-9]{6,14}$/.test(value);
                case "url":
                    return /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(value);
                case "color":
                    return /^(#(?:[0-9a-f]{2}){2,4}$|(#[0-9a-f]{3}$)|(rgb|hsl)a?\((-?\d+%?[,\s]+){2,3}\s*[\d\.]+%?\)$)/i.test(
                        value
                    );
                default:
                    return true;
            }
        },
    },
});
</script>

<style scoped lang="scss">
.string-cell {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    transition: all 0.3s;
    position: relative;

    &.outlined {
        border: 1px solid #d9d9d9;
        border-radius: 4px;

        &:not(.readonly) {
            cursor: pointer;

            &:hover {
                border-color: #40a9ff;
            }

            &:active {
                border-color: #096dd9;
            }
        }

        &.readonly {
            background-color: #f5f5f5;
            border-color: #d9d9d9;
            cursor: not-allowed;
        }
    }

    &.left {
        justify-content: flex-start;
    }

    &.center {
        justify-content: center;
    }

    &.right {
        justify-content: flex-end;
    }

    &.invalid-value {
        background-color: #ffebee;
    }

    .borderless-input {
        border: none;
        outline: none;
        background: transparent;
        width: 100%;
        text-align: inherit;

        &.invalid-input {
            border: 1px solid #f44336;
        }
    }

    .text-wrapper {
        width: 100%;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .overlay-actions {
        position: absolute;
        top: 2px;
        right: 2px;
        display: flex;
        gap: 4px;
        .whatsapp-button {
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
                width: 16px;
                height: 16px;
            }
        }
    }
}
</style>
<style lang="scss">
.table-mail-composer-modal {
    #mail-composer {
        padding: 0 !important;
        .title-wrapper {
            display: none;
        }
        .email-editor {
            .body {
                padding: 0 !important;
            }
        }
    }
}
</style>
